/* ==========================================================================
   pdp-tabbed-info.js
   Tab shell for sections/pdp-tabbed-info.liquid.

   Hand-written asset on purpose: Alpine components live in src/components/*.ts and compile
   into assets/fsg-* chunks, which .gitignore excludes from the GitHub -> Shopify sync. A
   registered Alpine.data('pdpTabs') would therefore be undefined on the live theme. Alpine is
   still used *inside* the panels (accordionGroup, x-collapse) — that comes from the bundle.

   Handles: click + keyboard tab switching (roving tabindex, arrow/Home/End), legacy anchor
   deep-links (#ingredients, #faq, #real-premium-coffee) mapped to tabs, theme-editor
   section/block selection, and re-init after an ajax DOM swap (variant switch). Emits a
   `pdp-tabs:change` CustomEvent for GTM / Convert.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-pdp-tabs]';
  var READY_ATTR = 'data-pdp-tabs-ready';

  // Remembers the tab the shopper had open, keyed by section id, so that when the DOM is swapped
  // out from under us (see the MutationObserver below) the rebuilt section can be put back the
  // way they left it. Parked on window rather than in closure scope: if this asset is ever
  // loaded twice, both copies must share one store.
  var lastActive = window.__pdpTabsLastActive || (window.__pdpTabsLastActive = {});

  function tabsOf(root) {
    return Array.prototype.slice.call(root.querySelectorAll('.pdp-tabs__tab'));
  }

  function panelsOf(root) {
    return Array.prototype.slice.call(root.querySelectorAll('.pdp-tabs__panel'));
  }

  function sectionKey(root) {
    return root.getAttribute('data-section-id') || 'default';
  }

  function tabByAnchor(root, hash) {
    if (!hash) return null;
    var name = hash.charAt(0) === '#' ? hash.slice(1) : hash;
    if (!name) return null;

    var matches = tabsOf(root).filter(function (tab) {
      return tab.getAttribute('data-anchor') === name;
    });
    return matches[0] || null;
  }

  function remember(root, tab) {
    lastActive[sectionKey(root)] = {
      anchor: tab.getAttribute('data-anchor') || '',
      tabId: tab.getAttribute('data-tab') || '',
      label: (tab.textContent || '').trim().toLowerCase()
    };
  }

  function rememberedTab(root) {
    var saved = lastActive[sectionKey(root)];
    if (!saved) return null;

    var tabs = tabsOf(root);
    var byAnchor = null;
    var byId = null;
    var byLabel = null;

    for (var i = 0; i < tabs.length; i++) {
      var tab = tabs[i];
      if (!byAnchor && saved.anchor && tab.getAttribute('data-anchor') === saved.anchor) byAnchor = tab;
      if (!byId && saved.tabId && tab.getAttribute('data-tab') === saved.tabId) byId = tab;
      if (!byLabel && saved.label && (tab.textContent || '').trim().toLowerCase() === saved.label) byLabel = tab;
    }

    // Anchor is the most stable identity across products; data-tab is a block id, which only
    // matches within the same template; the label is a last resort for reordered blocks.
    return byAnchor || byId || byLabel;
  }

  // Horizontal-only cousin of the scrollIntoView in activate(): nudges the nav's own scroll so
  // the active pill is visible WITHOUT moving page scroll. Used when restoring state, which
  // happens while the shopper is still up at the buy box — scrollIntoView({block: 'nearest'})
  // would yank the page down to the tabs whenever the section sits below the fold.
  function keepPillInNav(root, tab) {
    var nav = tab.parentNode;
    if (!nav || typeof nav.scrollLeft !== 'number' || !nav.getBoundingClientRect) return;
    if (nav.scrollWidth <= nav.clientWidth) return;

    var navRect = nav.getBoundingClientRect();
    var tabRect = tab.getBoundingClientRect();

    if (tabRect.left < navRect.left) {
      nav.scrollLeft -= navRect.left - tabRect.left;
    } else if (tabRect.right > navRect.right) {
      nav.scrollLeft += tabRect.right - navRect.right;
    }
  }

  // opts.restore marks an internal state resync rather than a shopper action: no focus, no hash
  // rewrite, no page scroll, and no `pdp-tabs:change` event — firing one would bill analytics for
  // a phantom tab click on every variant switch.
  function activate(root, tab, options) {
    var opts = options || {};
    if (!tab) return false;

    var id = tab.getAttribute('data-tab');

    tabsOf(root).forEach(function (item) {
      var on = item === tab;
      item.classList.toggle('is-active', on);
      item.setAttribute('aria-selected', on ? 'true' : 'false');
      item.tabIndex = on ? 0 : -1;
    });

    panelsOf(root).forEach(function (panel) {
      var on = panel.getAttribute('data-tab') === id;
      panel.classList.toggle('is-active', on);
      // The `hidden` attribute (not just the class) is what removes an inactive panel from the
      // accessibility tree. `.pdp-tabs__panel.is-active` outranks the UA `[hidden]` rule, but we
      // keep both in sync anyway.
      panel.hidden = !on;
    });

    remember(root, tab);

    if (opts.focus) tab.focus();

    if (opts.restore) {
      keepPillInNav(root, tab);
    } else if (tab.scrollIntoView) {
      // Keep the active pill in view inside the mobile horizontal scroller.
      try {
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } catch (e) {
        /* older browsers ignore the options object */
      }
    }

    var anchor = tab.getAttribute('data-anchor');
    if (opts.updateHash && anchor && window.history && window.history.replaceState) {
      // replaceState, not pushState — the back button should leave the PDP, not walk the tabs.
      try {
        window.history.replaceState(null, '', '#' + anchor);
      } catch (e) {
        /* blocked in some sandboxed contexts */
      }
    }

    if (opts.restore) return true;

    root.dispatchEvent(
      new CustomEvent('pdp-tabs:change', {
        bubbles: true,
        detail: {
          sectionId: root.getAttribute('data-section-id') || '',
          tabId: id,
          anchor: anchor || '',
          label: (tab.textContent || '').trim()
        }
      })
    );

    return true;
  }

  function init(root) {
    if (!root || root.getAttribute(READY_ATTR) === '1') return;
    root.setAttribute(READY_ATTR, '1');

    root.addEventListener('click', function (event) {
      var node = event.target;
      if (!node || typeof node.closest !== 'function') return;

      var tab = node.closest('.pdp-tabs__tab');
      if (tab && root.contains(tab)) activate(root, tab, { updateHash: true });
    });

    root.addEventListener('keydown', function (event) {
      var node = event.target;
      if (!node || typeof node.closest !== 'function') return;

      var tab = node.closest('.pdp-tabs__tab');
      if (!tab || !root.contains(tab)) return;

      var tabs = tabsOf(root);
      var index = tabs.indexOf(tab);
      if (index === -1) return;

      var next = -1;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (index + 1) % tabs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (index - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      activate(root, tabs[next], { focus: true, updateHash: true });
    });

    // Deep link on load: /products/<handle>#ingredients opens the Ingredients tab.
    var initial = tabByAnchor(root, window.location.hash);
    if (initial) {
      activate(root, initial, {});
      if (root.scrollIntoView) root.scrollIntoView({ block: 'start' });
      return;
    }

    // No deep link. If this root replaces one that was torn out by an ajax swap, the server HTML
    // has come back with the *first* tab active — put the shopper's tab back. The variant swap
    // rewrites the URL without a hash, so the deep-link branch above never competes for it.
    var previous = rememberedTab(root);
    if (previous) activate(root, previous, { restore: true });
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll(ROOT_SELECTOR), init);
  }

  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll(ROOT_SELECTOR));
  }

  // Document-level handlers are bound exactly once, even across theme-editor section reloads.
  if (!window.__pdpTabsBound) {
    window.__pdpTabsBound = true;

    // Same-page anchor links (e.g. the buy-box "Learn More" -> #ingredients) open the matching
    // tab instead of jumping to a section that no longer exists on the page.
    document.addEventListener('click', function (event) {
      var node = event.target;
      if (!node || typeof node.closest !== 'function') return;

      var link = node.closest('a[href^="#"]');
      if (!link) return;

      var all = roots();
      for (var i = 0; i < all.length; i++) {
        var tab = tabByAnchor(all[i], link.getAttribute('href'));
        if (tab) {
          event.preventDefault();
          activate(all[i], tab, { updateHash: true });
          all[i].scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
    });

    window.addEventListener('hashchange', function () {
      roots().forEach(function (root) {
        var tab = tabByAnchor(root, window.location.hash);
        if (tab) activate(root, tab, {});
      });
    });

    // Theme editor: re-init a re-rendered section, and reveal the tab whose block the merchant
    // just selected in the sidebar.
    document.addEventListener('shopify:section:load', initAll);

    document.addEventListener('shopify:block:select', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var panel = target.classList && target.classList.contains('pdp-tabs__panel')
        ? target
        : target.closest('.pdp-tabs__panel');
      if (!panel) return;

      var root = panel.closest(ROOT_SELECTOR);
      if (!root) return;

      var tabs = tabsOf(root).filter(function (tab) {
        return tab.getAttribute('data-tab') === panel.getAttribute('data-tab');
      });
      if (tabs[0]) activate(root, tabs[0], {});
    });

    // Re-init after an ajax DOM swap. Switching product variant on the v2 PDP is neither a
    // navigation nor a Section Rendering API call: the variant links call
    // `$store.ajaxPage.setContent()` (src/components/update-page), which assigns
    // `main.innerHTML` wholesale. That destroys this section's root along with the listeners
    // init() bound to it, and a <script> arriving via innerHTML never executes — so neither
    // DOMContentLoaded nor shopify:section:load fires, and the rebuilt pills come back dead.
    // Alpine survives the same swap because it runs an observer of its own; this is ours. Also
    // covers the collection-menu swap, which drives the same store.
    if (window.MutationObserver) {
      var sweepQueued = false;

      var sweep = function () {
        sweepQueued = false;
        initAll(); // Idempotent: init() bails on anything already flagged ready.
      };

      var observer = new MutationObserver(function (records) {
        if (sweepQueued) return;

        for (var i = 0; i < records.length; i++) {
          if (!records[i].addedNodes || !records[i].addedNodes.length) continue;

          // One cheap selector per batch of records — a single innerHTML write produces many.
          if (!document.querySelector(ROOT_SELECTOR + ':not([' + READY_ATTR + '="1"])')) return;

          sweepQueued = true;
          if (window.requestAnimationFrame) window.requestAnimationFrame(sweep);
          else window.setTimeout(sweep, 0);
          return;
        }
      });

      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
